import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  DollarSign, TrendingUp, AlertTriangle, CheckCircle, Target,
  Building, CreditCard, PiggyBank, Wallet, BarChart3, Calculator,
  RefreshCw, ChevronRight, ArrowUpRight, ArrowDownRight, Shield,
  GraduationCap, Wrench, Rocket, Briefcase, Users, Clock, Info,
  PieChart, LineChart, Landmark, Home, Star, Zap, FileText,
  AlertCircle, Lock, Unlock, Eye, ChevronDown, ChevronUp, ShieldAlert,
  MapPin, Calendar, Award, BookOpen, User
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { PieChart as RechartsPieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart as RechartsLineChart, Line } from 'recharts';
import OBBBADisclaimer from '../components/OBBBADisclaimer';
import WorkforcePellCallout from '../components/WorkforcePellCallout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Financial Disclaimer Component
const FinancialDisclaimer = () => (
  <div className="flex items-center gap-2 px-3 py-2 rounded-full border border-blue-500/25 bg-blue-500/[0.07] mb-4"
    data-testid="financial-disclaimer">
    <ShieldAlert className="w-3.5 h-3.5 text-blue-200 shrink-0" />
    <p className="text-xs text-blue-50/85">
      Estimates for planning, not financial, tax or legal advice, check anything binding with an aid office or advisor.
    </p>
  </div>
);

// Risk colors
const RISK_COLORS = {
  low: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', color: '#10B981', label: 'On Track' },
  moderate: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', color: '#F59E0B', label: 'Monitor' },
  high: { bg: 'bg-red-500/20', text: 'text-red-400', color: '#EF4444', label: 'Critical Gap' }
};

// Scenario Comparison Tool Component
const ScenarioComparisonTool = () => {
  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
  
  const [scenarios, setScenarios] = useState([
    { id: 1, name: 'Public In-State', tuition: 12000, roomBoard: 11500, aid: 5000, savings: 500, years: 4 },
    { id: 2, name: 'Public Out-of-State', tuition: 28000, roomBoard: 14000, aid: 3000, savings: 500, years: 4 },
  ]);

  const addScenario = () => {
    if (scenarios.length >= 4) { toast.info('Maximum 4 scenarios'); return; }
    setScenarios([...scenarios, { 
      id: Date.now(), name: `Scenario ${scenarios.length + 1}`, 
      tuition: 20000, roomBoard: 13000, aid: 4000, savings: 500, years: 4 
    }]);
  };

  const removeScenario = (id) => {
    if (scenarios.length <= 1) return;
    setScenarios(scenarios.filter(s => s.id !== id));
  };

  const updateScenario = (id, field, value) => {
    setScenarios(scenarios.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const calcTotal = (s) => (s.tuition + s.roomBoard) * s.years;
  const calcNetCost = (s) => Math.max(0, calcTotal(s) - (s.aid * s.years));
  const calcMonthlySavings = (s) => s.savings;
  const calcSavingsTotal = (s) => s.savings * 12 * s.years;
  const calcGap = (s) => Math.max(0, calcNetCost(s) - calcSavingsTotal(s));

  const SCENARIO_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b'];

  const comparisonData = scenarios.map((s, i) => ({
    name: s.name,
    'Total Cost': calcTotal(s),
    'After Aid': calcNetCost(s),
    'Your Savings': calcSavingsTotal(s),
    'Funding Gap': calcGap(s),
    fill: SCENARIO_COLORS[i]
  }));

  return (
    <div className="space-y-6">
      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Calculator className="w-5 h-5 text-purple-400" />
            Scenario Comparison Tool
          </h3>
          <Button size="sm" variant="outline" onClick={addScenario} className="border-slate-600 text-slate-300" data-testid="add-scenario-btn">
            + Add Scenario
          </Button>
        </div>

        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${scenarios.length}, 1fr)` }}>
          {scenarios.map((s, idx) => (
            <div key={s.id} className="p-4 rounded-xl border border-slate-700 bg-slate-800/50 space-y-3" data-testid={`scenario-card-${idx}`}>
              <div className="flex items-center justify-between">
                <input 
                  value={s.name} 
                  onChange={(e) => updateScenario(s.id, 'name', e.target.value)}
                  className="bg-transparent text-white font-semibold text-sm border-b border-slate-600 focus:border-purple-400 outline-none w-full mr-2"
                />
                {scenarios.length > 1 && (
                  <button onClick={() => removeScenario(s.id)} className="text-slate-300 hover:text-red-400 text-xs">Remove</button>
                )}
              </div>
              <div className="w-full h-1 rounded" style={{ backgroundColor: SCENARIO_COLORS[idx] }} />
              
              {[
                { label: 'Annual Tuition', field: 'tuition', val: s.tuition },
                { label: 'Room & Board', field: 'roomBoard', val: s.roomBoard },
                { label: 'Annual Aid/Grants', field: 'aid', val: s.aid },
                { label: 'Monthly Savings', field: 'savings', val: s.savings },
              ].map(item => (
                <div key={item.field}>
                  <label className="text-xs text-slate-300">{item.label}</label>
                  <input 
                    type="text"
                    inputMode="numeric"
                    value={item.val} 
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^\d*$/.test(v)) updateScenario(s.id, item.field, v === '' ? 0 : parseInt(v));
                    }}
                    className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-white text-sm"
                  />
                </div>
              ))}
              <div>
                <label className="text-xs text-slate-300">Years</label>
                <select value={s.years} onChange={(e) => updateScenario(s.id, 'years', parseInt(e.target.value))}
                  className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-white text-sm">
                  {[2,4,5,6].map(y => <option key={y} value={y}>{y} years</option>)}
                </select>
              </div>

              <div className="pt-3 border-t border-slate-700 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-300">Total Cost</span>
                  <span className="text-white font-medium">{fmt(calcTotal(s))}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-300">After Aid</span>
                  <span className="text-emerald-400 font-medium">{fmt(calcNetCost(s))}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-300">Your Savings</span>
                  <span className="text-blue-400 font-medium">{fmt(calcSavingsTotal(s))}</span>
                </div>
                <div className="flex justify-between text-sm font-semibold">
                  <span className="text-slate-300">Funding Gap</span>
                  <span className={calcGap(s) > 0 ? 'text-red-400' : 'text-emerald-400'}>
                    {calcGap(s) > 0 ? fmt(calcGap(s)) : 'Covered!'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </GlassCard>

      {/* Comparison Chart */}
      <GlassCard className="p-6">
        <h3 className="text-white font-semibold mb-4">Side-by-Side Comparison</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={comparisonData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
            <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={v => fmt(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
            <Legend />
            <Bar dataKey="Total Cost" fill="#ef4444" radius={[4,4,0,0]} />
            <Bar dataKey="After Aid" fill="#3b82f6" radius={[4,4,0,0]} />
            <Bar dataKey="Your Savings" fill="#10b981" radius={[4,4,0,0]} />
            <Bar dataKey="Funding Gap" fill="#f59e0b" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </GlassCard>
    </div>
  );
};

// Path icons and configurations
const PATH_CONFIGS = {
  college: { icon: GraduationCap, label: 'College Bound', gradient: 'from-blue-600 to-cyan-600', color: 'blue' },
  trade: { icon: Wrench, label: 'Skilled Trades', gradient: 'from-amber-600 to-orange-600', color: 'amber' },
  military: { icon: Shield, label: 'Military Service', gradient: 'from-green-600 to-emerald-600', color: 'green' },
  entrepreneurship: { icon: Rocket, label: 'Entrepreneurship', gradient: 'from-purple-600 to-pink-600', color: 'purple' },
  workforce: { icon: Briefcase, label: 'Direct Workforce', gradient: 'from-slate-600 to-zinc-600', color: 'slate' }
};

// Account type icons
const ACCOUNT_ICONS = {
  checking: Wallet,
  savings: PiggyBank,
  '529': GraduationCap,
  roth_ira: Landmark,
  brokerage: BarChart3,
  custodial: Users,
  high_yield_savings: TrendingUp
};

// Manual Financial Entry Modal, Comprehensive
const ManualFinancialEntryModal = ({ open, onClose, onSubmit, existingData }) => {
  const details = existingData?.financial_details || {};
  const [formData, setFormData] = useState({
    // Education Savings
    savings_529: details.savings_529 || '',
    custodial_utma: details.custodial_utma || '',
    coverdell_esa: details.coverdell_esa || '',
    education_bonds: details.education_bonds || '',
    // Liquid Assets
    checking: details.checking || '',
    savings_account: details.savings_account || '',
    money_market_cds: details.money_market_cds || '',
    // Investments
    brokerage: details.brokerage || '',
    stocks_bonds: details.stocks_bonds || '',
    mutual_funds_etfs: details.mutual_funds_etfs || '',
    crypto: details.crypto || '',
    // Retirement (earmarked)
    retirement_401k_ira: details.retirement_401k_ira || '',
    // Property
    real_estate_equity: details.real_estate_equity || '',
    // Income & Savings
    household_income: details.household_income || '',
    monthly_education_savings: details.monthly_education_savings || '',
    expected_financial_aid: details.expected_financial_aid || '',
    expected_scholarships: details.expected_scholarships || '',
    // Liabilities
    existing_student_loans: details.existing_student_loans || '',
    credit_card_debt: details.credit_card_debt || '',
    other_debt: details.other_debt || '',
    // Credit
    credit_score: (() => {
      const s = existingData?.credit_profile?.credit_score;
      if (!s) return '';
      if (s >= 800) return '800';
      if (s >= 750) return '750';
      if (s >= 700) return '720';
      if (s >= 650) return '670';
      return '600';
    })(),
  });
  const [submitting, setSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState('education');

  useEffect(() => {
    if (open && existingData?.financial_details) {
      const d = existingData.financial_details;
      setFormData(prev => ({
        ...prev,
        savings_529: d.savings_529 || '',
        custodial_utma: d.custodial_utma || '',
        coverdell_esa: d.coverdell_esa || '',
        education_bonds: d.education_bonds || '',
        checking: d.checking || '',
        savings_account: d.savings_account || '',
        money_market_cds: d.money_market_cds || '',
        brokerage: d.brokerage || '',
        stocks_bonds: d.stocks_bonds || '',
        mutual_funds_etfs: d.mutual_funds_etfs || '',
        crypto: d.crypto || '',
        retirement_401k_ira: d.retirement_401k_ira || '',
        real_estate_equity: d.real_estate_equity || '',
        household_income: d.household_income || '',
        monthly_education_savings: d.monthly_education_savings || '',
        expected_financial_aid: d.expected_financial_aid || '',
        expected_scholarships: d.expected_scholarships || '',
        existing_student_loans: d.existing_student_loans || '',
        credit_card_debt: d.credit_card_debt || '',
        other_debt: d.other_debt || '',
        credit_score: (() => {
          const s = existingData?.credit_profile?.credit_score;
          if (!s) return '';
          if (s >= 800) return '800';
          if (s >= 750) return '750';
          if (s >= 700) return '720';
          if (s >= 650) return '670';
          return '600';
        })(),
      }));
    }
  }, [open, existingData]);

  const num = (v) => parseFloat(v) || 0;

  const totals = {
    education: num(formData.savings_529) + num(formData.custodial_utma) + num(formData.coverdell_esa) + num(formData.education_bonds),
    liquid: num(formData.checking) + num(formData.savings_account) + num(formData.money_market_cds),
    investments: num(formData.brokerage) + num(formData.stocks_bonds) + num(formData.mutual_funds_etfs) + num(formData.crypto),
    retirement: num(formData.retirement_401k_ira),
    property: num(formData.real_estate_equity),
    liabilities: num(formData.existing_student_loans) + num(formData.credit_card_debt) + num(formData.other_debt),
  };
  totals.total_assets = totals.education + totals.liquid + totals.investments + totals.retirement + totals.property;
  totals.net_worth = totals.total_assets - totals.liabilities;

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const score = parseInt(formData.credit_score) || 0;
      let tier = 'good';
      if (score >= 750) tier = 'excellent';
      else if (score >= 700) tier = 'good';
      else if (score >= 650) tier = 'fair';
      else if (score > 0) tier = 'poor';

      await onSubmit({
        total_education_savings: totals.education,
        total_available_funds: totals.liquid + totals.investments,
        monthly_savings_rate: num(formData.monthly_education_savings),
        estimated_credit_score: tier,
        exact_credit_score: score > 0 ? score : null,
        financial_details: {
          savings_529: num(formData.savings_529),
          custodial_utma: num(formData.custodial_utma),
          coverdell_esa: num(formData.coverdell_esa),
          education_bonds: num(formData.education_bonds),
          checking: num(formData.checking),
          savings_account: num(formData.savings_account),
          money_market_cds: num(formData.money_market_cds),
          brokerage: num(formData.brokerage),
          stocks_bonds: num(formData.stocks_bonds),
          mutual_funds_etfs: num(formData.mutual_funds_etfs),
          crypto: num(formData.crypto),
          retirement_401k_ira: num(formData.retirement_401k_ira),
          real_estate_equity: num(formData.real_estate_equity),
          household_income: num(formData.household_income),
          monthly_education_savings: num(formData.monthly_education_savings),
          expected_financial_aid: num(formData.expected_financial_aid),
          expected_scholarships: num(formData.expected_scholarships),
          existing_student_loans: num(formData.existing_student_loans),
          credit_card_debt: num(formData.credit_card_debt),
          other_debt: num(formData.other_debt),
        },
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const sections = [
    { id: 'education', label: 'Education Savings', icon: GraduationCap, color: 'emerald' },
    { id: 'liquid', label: 'Cash & Bank', icon: Wallet, color: 'blue' },
    { id: 'investments', label: 'Investments', icon: TrendingUp, color: 'purple' },
    { id: 'income', label: 'Income & Aid', icon: DollarSign, color: 'cyan' },
    { id: 'liabilities', label: 'Liabilities', icon: CreditCard, color: 'red' },
    { id: 'credit', label: 'Credit', icon: Shield, color: 'amber' },
  ];

  const dollarInput = (field, placeholder) => (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-sm">$</span>
      <input
        type="text"
        inputMode="decimal"
        value={formData[field]}
        onChange={(e) => {
          const val = e.target.value;
          if (val === '' || /^\d*\.?\d*$/.test(val)) {
            setFormData(f => ({ ...f, [field]: val }));
          }
        }}
        placeholder={placeholder || '0'}
        className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-7 pr-3 py-2 text-white text-sm focus:border-emerald-500 outline-none"
        data-testid={`input-${field}`}
      />
    </div>
  );

  const fieldRow = (label, field, placeholder) => (
    <div className="flex items-center justify-between gap-4">
      <label className="text-sm text-slate-300 whitespace-nowrap">{label}</label>
      <div className="w-44">
        {dollarInput(field, placeholder)}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a1729]/70 backdrop-blur-sm p-4" data-testid="manual-financial-modal">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-5 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <PiggyBank className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Your Financial Picture</h2>
                <p className="text-xs text-slate-300">Fill in what you know, update anytime as things change</p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-300 hover:text-white text-xl">&times;</button>
          </div>

          {/* Section tabs */}
          <div className="flex gap-1 mt-4 overflow-x-auto pb-1">
            {sections.map(s => {
              const Icon = s.icon;
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveSection(s.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                    activeSection === s.id 
                      ? `bg-${s.color}-500/20 text-${s.color}-400 border border-${s.color}-500/30` 
                      : 'text-slate-300 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {s.label}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1">
          {activeSection === 'education' && (
            <div className="space-y-3">
              {fieldRow("529 Plans", "savings_529")}
              {fieldRow("Custodial (UTMA/UGMA)", "custodial_utma")}
              {fieldRow("Coverdell ESA", "coverdell_esa")}
              {fieldRow("Education Savings Bonds", "education_bonds")}
              <div className="pt-3 border-t border-slate-700 flex justify-between">
                <span className="text-sm font-medium text-slate-300">Education Total</span>
                <span className="text-sm font-bold text-emerald-400">{fmt(totals.education)}</span>
              </div>
            </div>
          )}

          {activeSection === 'liquid' && (
            <div className="space-y-3">
              {fieldRow("Checking Accounts", "checking")}
              {fieldRow("Savings Accounts", "savings_account")}
              {fieldRow("Money Market / CDs", "money_market_cds")}
              <div className="pt-3 border-t border-slate-700 flex justify-between">
                <span className="text-sm font-medium text-slate-300">Liquid Total</span>
                <span className="text-sm font-bold text-blue-400">{fmt(totals.liquid)}</span>
              </div>
            </div>
          )}

          {activeSection === 'investments' && (
            <div className="space-y-3">
              {fieldRow("Brokerage Accounts", "brokerage")}
              {fieldRow("Stocks / Bonds", "stocks_bonds")}
              {fieldRow("Mutual Funds / ETFs", "mutual_funds_etfs")}
              {fieldRow("Crypto", "crypto")}
              {fieldRow("401(k) / IRA (for education)", "retirement_401k_ira")}
              {fieldRow("Real Estate Equity", "real_estate_equity")}
              <div className="pt-3 border-t border-slate-700 flex justify-between">
                <span className="text-sm font-medium text-slate-300">Investment Total</span>
                <span className="text-sm font-bold text-purple-400">{fmt(totals.investments + totals.retirement + totals.property)}</span>
              </div>
            </div>
          )}

          {activeSection === 'income' && (
            <div className="space-y-3">
              {fieldRow("Annual Household Income", "household_income")}
              {fieldRow("Monthly Savings for Education", "monthly_education_savings")}
              {fieldRow("Expected Financial Aid (annual)", "expected_financial_aid")}
              {fieldRow("Expected Scholarships (annual)", "expected_scholarships")}
            </div>
          )}

          {activeSection === 'liabilities' && (
            <div className="space-y-3">
              {fieldRow("Existing Student Loans", "existing_student_loans")}
              {fieldRow("Credit Card Debt", "credit_card_debt")}
              {fieldRow("Other Debt", "other_debt")}
              <div className="pt-3 border-t border-slate-700 flex justify-between">
                <span className="text-sm font-medium text-slate-300">Total Liabilities</span>
                <span className="text-sm font-bold text-red-400">{fmt(totals.liabilities)}</span>
              </div>
            </div>
          )}

          {activeSection === 'credit' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-4">
                <label className="text-sm text-slate-300">Credit Score</label>
                <div className="w-44">
                  <select
                    value={formData.credit_score}
                    onChange={(e) => setFormData(f => ({ ...f, credit_score: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 outline-none"
                    data-testid="select-credit-score"
                  >
                    <option value="">Select range</option>
                    <option value="800">Excellent (800+)</option>
                    <option value="750">Very Good (750-799)</option>
                    <option value="720">Good (700-749)</option>
                    <option value="670">Fair (650-699)</option>
                    <option value="600">Poor (Below 650)</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer with summary */}
        <div className="p-5 border-t border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-between mb-4 text-xs">
            <div className="flex gap-4">
              <span className="text-slate-300">Assets: <span className="text-white font-medium">{fmt(totals.total_assets)}</span></span>
              <span className="text-slate-300">Liabilities: <span className="text-red-400 font-medium">{fmt(totals.liabilities)}</span></span>
              <span className="text-slate-300">Net: <span className={`font-medium ${totals.net_worth >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmt(totals.net_worth)}</span></span>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300" data-testid="cancel-manual-entry">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-manual-entry">
              {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Format percentage
const formatPercent = (value) => {
  if (value === null || value === undefined) return '0%';
  return `${value.toFixed(1)}%`;
};

// KPI Card
const KPICard = ({ icon: Icon, label, value, subLabel, trend, color = 'blue', size = 'normal', onClick }) => (
  <GlassCard 
    className={`${size === 'large' ? 'p-6' : 'p-4'} ${onClick ? 'cursor-pointer hover:border-slate-600 transition-all' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div className={`${size === 'large' ? 'w-12 h-12' : 'w-10 h-10'} rounded-xl bg-${color}-500/20 flex items-center justify-center`}>
        <Icon className={`${size === 'large' ? 'w-6 h-6' : 'w-5 h-5'} text-${color}-400`} />
      </div>
      {trend !== undefined && (
        <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
          trend >= 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
        }`}>
          {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    <p className={`${size === 'large' ? 'text-3xl' : 'text-2xl'} font-bold text-white mt-3`}>{value}</p>
    <p className="text-sm text-slate-300 mt-1">{label}</p>
    {subLabel && <p className="text-xs text-slate-300 mt-0.5">{subLabel}</p>}
  </GlassCard>
);

// Child Switcher Component - Horizontal tabs at top
const ChildSwitcher = ({ children, selectedChild, onSelectChild }) => {
  if (!children || children.length === 0) return null;
  
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-5 h-5 text-slate-300" />
        <span className="text-sm text-slate-300 font-medium">Select Child to View Financial Details</span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {/* All Children option */}
        <button
          onClick={() => onSelectChild(null)}
          className={`flex-shrink-0 px-4 py-3 rounded-xl transition-all flex items-center gap-3 ${
            !selectedChild 
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg' 
              : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 border border-slate-700'
          }`}
          data-testid="child-switcher-all"
        >
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            !selectedChild ? 'bg-white/20' : 'bg-slate-700'
          }`}>
            <Users className="w-5 h-5" />
          </div>
          <div className="text-left">
            <p className="font-medium">All Children</p>
            <p className={`text-xs ${!selectedChild ? 'text-white/70' : 'text-slate-300'}`}>Family Overview</p>
          </div>
        </button>
        
        {/* Individual children */}
        {children.map((child) => {
          const isSelected = selectedChild?.child_id === child.child_id;
          const pathConfig = PATH_CONFIGS[child.locked_path?.path_type];
          const PathIcon = pathConfig?.icon || Target;
          const risk = RISK_COLORS[child.risk_level] || RISK_COLORS.moderate;
          
          return (
            <button
              key={child.child_id}
              onClick={() => onSelectChild(child)}
              className={`flex-shrink-0 px-4 py-3 rounded-xl transition-all flex items-center gap-3 min-w-[200px] ${
                isSelected 
                  ? `bg-gradient-to-r ${pathConfig?.gradient || 'from-blue-600 to-purple-600'} text-white shadow-lg` 
                  : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 border border-slate-700'
              }`}
              data-testid={`child-switcher-${child.child_id}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold ${
                isSelected ? 'bg-white/20' : `bg-gradient-to-br ${pathConfig?.gradient || 'from-slate-600 to-slate-700'} text-white`
              }`}>
                {child.child_name?.charAt(0) || '?'}
              </div>
              <div className="text-left flex-1">
                <p className="font-medium">{child.child_name}</p>
                <div className="flex items-center gap-2 text-xs">
                  <PathIcon className="w-3 h-3" />
                  <span className={isSelected ? 'text-white/70' : 'text-slate-300'}>
                    {pathConfig?.label || 'No path selected'}
                  </span>
                </div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs ${isSelected ? 'bg-white/20 text-white' : `${risk.bg} ${risk.text}`}`}>
                {risk.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Selected Child Career Details Panel
const ChildCareerPanel = ({ child }) => {
  if (!child) return null;
  
  const pathConfig = PATH_CONFIGS[child.locked_path?.path_type];
  const PathIcon = pathConfig?.icon || Target;
  const risk = RISK_COLORS[child.risk_level] || RISK_COLORS.moderate;
  
  return (
    <GlassCard className="p-6 mb-6 border-2 border-blue-500/30">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-2xl bg-gradient-to-br ${pathConfig?.gradient || 'from-slate-600 to-slate-700'}`}>
            {child.child_name?.charAt(0) || '?'}
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">{child.child_name}</h2>
            <p className="text-slate-300">Grade {child.grade_level || '-'}</p>
          </div>
        </div>
        <span className={`px-4 py-2 rounded-lg text-sm font-medium ${risk.bg} ${risk.text}`}>
          {risk.label}
        </span>
      </div>
      
      {child.locked_path ? (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Path Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <PathIcon className="w-5 h-5 text-blue-400" />
              Selected Career Path
            </h3>
            
            <div className="p-4 rounded-xl bg-slate-800/50 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Institution</span>
                <span className="text-white font-medium">{child.locked_path.institution_name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Program/Major</span>
                <span className="text-white font-medium">{child.locked_path.major_or_track || 'General'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Path Type</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${pathConfig?.gradient} text-white`}>
                  {pathConfig?.label || 'Unknown'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Status</span>
                <span className={`px-3 py-1 rounded text-xs font-medium ${
                  child.locked_path.lock_status === 'finalized' ? 'bg-emerald-500/20 text-emerald-400' :
                  child.locked_path.lock_status === 'hard_lock' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-slate-700 text-slate-300'
                }`}>
                  {child.locked_path.lock_status?.replace('_', ' ').toUpperCase() || 'EXPLORING'}
                </span>
              </div>
              {child.locked_path.location && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Location</span>
                  <span className="text-white font-medium flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {child.locked_path.location}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* Financial Profile for this path */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              Financial Profile
            </h3>
            
            {child.path_financial_profile ? (
              <div className="p-4 rounded-xl bg-slate-800/50 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Total Program Cost</span>
                  <span className="text-2xl font-bold text-white">
                    {formatCurrency(child.path_financial_profile.total_program_cost)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Annual Cost</span>
                  <span className="text-white font-medium">
                    {formatCurrency(child.path_financial_profile.annual_cost)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Duration</span>
                  <span className="text-white font-medium">
                    {child.path_financial_profile.duration_years || 4} years
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Expected ROI</span>
                  <span className="text-emerald-400 font-medium">
                    {child.path_financial_profile.break_even_years?.toFixed(1) || '-'} yrs to break-even
                  </span>
                </div>
                
                {/* Funding Gap Progress */}
                {child.funding_gap_analysis && (
                  <div className="pt-3 border-t border-slate-700">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-slate-300">Funding Progress</span>
                      <span className={`font-medium ${risk.text}`}>
                        {formatPercent(100 - child.funding_gap_analysis.gap_percentage)} funded
                      </span>
                    </div>
                    <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${100 - child.funding_gap_analysis.gap_percentage}%` }}
                        transition={{ duration: 1 }}
                        className="h-full bg-gradient-to-r from-emerald-500 to-blue-500"
                      />
                    </div>
                    <p className="text-xs text-slate-300 mt-2">
                      Gap: {formatCurrency(child.funding_gap_analysis.funding_gap)} remaining
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-6 rounded-xl bg-slate-800/50 text-center">
                <DollarSign className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-300">Financial profile not yet calculated</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="p-8 rounded-xl bg-slate-800/30 text-center">
          <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-white font-semibold mb-2">No Career Path Selected</h3>
          <p className="text-slate-300 text-sm">
            {child.child_name} hasn't selected a career path yet. Once they choose a path, 
            you'll see detailed financial projections here.
          </p>
        </div>
      )}
    </GlassCard>
  );
};

// Linked Account Card
const AccountCard = ({ account }) => {
  const Icon = ACCOUNT_ICONS[account.account_type] || Wallet;
  
  return (
    <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 hover:bg-slate-800 transition-colors">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
          <Icon className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <p className="text-white font-medium">{account.account_name}</p>
          <p className="text-slate-300 text-xs">{account.institution_name}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-white font-semibold">{formatCurrency(account.current_balance)}</p>
        {account.monthly_contribution > 0 && (
          <p className="text-emerald-400 text-xs">+{formatCurrency(account.monthly_contribution)}/mo</p>
        )}
      </div>
    </div>
  );
};

// Credit Score Gauge
const CreditScoreGauge = ({ score, rating }) => {
  const getScoreColor = (s) => {
    if (s >= 800) return '#10B981';
    if (s >= 740) return '#3B82F6';
    if (s >= 670) return '#F59E0B';
    if (s >= 580) return '#F97316';
    return '#EF4444';
  };
  
  const percentage = ((score - 300) / 550) * 100;
  
  return (
    <div className="text-center">
      <div className="relative w-40 h-40 mx-auto mb-4">
        <svg className="w-full h-full transform -rotate-90">
          <circle cx="80" cy="80" r="70" stroke="#1e293b" strokeWidth="12" fill="none" />
          <circle
            cx="80" cy="80" r="70"
            stroke={getScoreColor(score)}
            strokeWidth="12" fill="none"
            strokeDasharray={`${percentage * 4.4} 440`}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl font-bold text-white">{score}</span>
          <span className="text-sm text-slate-300">{rating}</span>
        </div>
      </div>
    </div>
  );
};

// Loan Eligibility Card
const LoanEligibilityCard = ({ loan, details }) => {
  const formatLoanName = (name) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };
  
  return (
    <div className={`p-4 rounded-xl border ${
      details.eligible 
        ? 'border-emerald-500/30 bg-emerald-500/5' 
        : 'border-slate-700 bg-slate-800/30 opacity-60'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-white font-medium">{formatLoanName(loan)}</span>
        <span className={`px-2 py-0.5 rounded text-xs ${
          details.eligible ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-300'
        }`}>
          {details.eligible ? 'Eligible' : 'Not Eligible'}
        </span>
      </div>
      {details.eligible && (
        <div className="space-y-1 text-sm">
          {details.max_amount && (
            <div className="flex justify-between">
              <span className="text-slate-300">Max Amount</span>
              <span className="text-white">{formatCurrency(details.max_amount)}</span>
            </div>
          )}
          {details.interest_rate && (
            <div className="flex justify-between">
              <span className="text-slate-300">Interest Rate</span>
              <span className="text-white">{details.interest_rate}%</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Component
export default function ParentFinancialIntelligence() {
  const { user, getAuthHeader } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedChild, setSelectedChild] = useState(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  
  useEffect(() => {
    fetchDashboard();
  }, []);
  
  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/parent/financial/dashboard`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching financial dashboard:', error);
      toast.error('Failed to load financial dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefreshAccounts = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/parent/financial/refresh-accounts`, {}, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Financial accounts refreshed');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to refresh accounts');
    } finally {
      setRefreshing(false);
    }
  };

  const handleManualUpdate = async (data) => {
    try {
      await axios.post(`${API}/parent/financial/manual-update`, data, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Financial data updated successfully!');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to save financial data');
      throw error;
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }
  
  const { portfolio, children_snapshots, family_summary } = dashboardData || {};
  const creditProfile = portfolio?.credit_profile;
  
  // Helper to check if path is military (net benefit, negative cost)
  const isMilitaryPath = (child) => child?.locked_path?.path_type === 'military';
  
  // Calculate display data based on selected child or all children
  const displaySummary = selectedChild ? {
    // For military paths, show $0 cost since they pay you
    total_education_exposure: isMilitaryPath(selectedChild) 
      ? 0 
      : Math.max(0, selectedChild.path_financial_profile?.total_program_cost || 0),
    total_current_savings: selectedChild.funding_gap_analysis?.current_savings_allocated || 0,
    total_funding_shortfall: Math.max(0, selectedChild.funding_gap_analysis?.funding_gap || 0),
    family_stability_score: family_summary?.family_stability_score || 0,
    // Extra field for military benefits
    military_benefits: isMilitaryPath(selectedChild) 
      ? Math.abs(selectedChild.path_financial_profile?.total_program_cost || 0) 
      : 0
  } : {
    // For family view, exclude negative costs from total
    total_education_exposure: Math.max(0, family_summary?.total_education_exposure || 0),
    total_current_savings: family_summary?.total_current_savings || 0,
    total_funding_shortfall: Math.max(0, family_summary?.total_funding_shortfall || 0),
    family_stability_score: family_summary?.family_stability_score || 0
  };
  
  // Prepare chart data - handle military negative costs
  const exposureChartData = children_snapshots
    ?.filter(c => c.path_financial_profile)
    .map(c => {
      const isMilitary = c.locked_path?.path_type === 'military';
      return {
        name: c.child_name?.split(' ')[0] || 'Child',
        cost: isMilitary ? 0 : Math.max(0, c.path_financial_profile?.total_program_cost || 0),
        funded: isMilitary 
          ? Math.abs(c.path_financial_profile?.total_program_cost || 0) // Show benefits as "funded"
          : (c.funding_gap_analysis?.current_savings_allocated || 0)
      };
    }) || [];
  
  const accountsChartData = portfolio?.linked_accounts?.map(a => ({
    name: a.account_name?.split(' ')[0] || a.account_type,
    value: a.current_balance || 0
  })) || [];
  
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];
  
  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Financial Disclaimer */}
        <FinancialDisclaimer />
        <OBBBADisclaimer dismissible />
        <WorkforcePellCallout />
        
        {/* Data Entry Banner */}
        <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-slate-700 bg-slate-800/50" data-testid="demo-data-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <PiggyBank className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-white font-medium text-sm">
                {portfolio?.data_source === 'plaid'
                  ? `Accounts synced via Plaid${portfolio?.linked_accounts?.length ? ` · ${portfolio.linked_accounts.length} accounts` : ''}`
                  : portfolio?.data_source === 'manual'
                  ? `Updated ${portfolio.last_synced ? new Date(portfolio.last_synced).toLocaleDateString() : 'recently'}`
                  : 'Enter your financial data to get started'
                }
              </p>
              {portfolio?.data_source === 'plaid' && (
                <p className="text-slate-300 text-xs mt-0.5">
                  {(portfolio.linked_accounts || []).map(a => a.institution_name).filter((v, i, s) => s.indexOf(v) === i).join(', ')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button 
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => setShowManualEntry(true)}
              data-testid="manual-entry-btn"
            >
              <PiggyBank className="w-4 h-4 mr-2" /> 
              {(portfolio?.data_source === 'manual' || portfolio?.data_source === 'plaid') ? 'Update Data' : 'Enter Data'}
            </Button>
            {portfolio?.data_source === 'plaid' ? (
              <Button
                size="sm"
                variant="outline"
                className="border-emerald-600/50 text-emerald-300 bg-emerald-500/10 cursor-default"
                data-testid="connect-plaid-btn"
              >
                <Landmark className="w-4 h-4 mr-1.5" /> Plaid Connected
              </Button>
            ) : (
              <div className="relative group">
                <Button 
                  size="sm"
                  variant="outline" 
                  className="border-slate-600 text-slate-300 cursor-default"
                  data-testid="connect-plaid-btn"
                >
                  <Landmark className="w-4 h-4 mr-1.5" /> Plaid
                </Button>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-700 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Coming soon
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Financial Intelligence</h1>
            <p className="text-slate-300 mt-1">
              {selectedChild 
                ? `Viewing financial details for ${selectedChild.child_name}`
                : 'Family education capital planning powered by AI'
              }
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={handleRefreshAccounts}
              disabled={refreshing}
              className="border-slate-600"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Sync Accounts
            </Button>
          </div>
        </div>
        
        {/* Child Switcher - Above financial breakdown */}
        <ChildSwitcher 
          children={children_snapshots}
          selectedChild={selectedChild}
          onSelectChild={setSelectedChild}
        />
        
        {/* Family/Child Summary KPIs - ABOVE Career Panel when child selected */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPICard
            icon={DollarSign}
            label={selectedChild 
              ? (isMilitaryPath(selectedChild) ? "Out-of-Pocket Cost" : "Program Cost")
              : "Total Education Exposure"
            }
            value={isMilitaryPath(selectedChild) ? "$0" : formatCurrency(displaySummary?.total_education_exposure)}
            subLabel={selectedChild 
              ? (isMilitaryPath(selectedChild) 
                ? "Military pays you!" 
                : selectedChild.locked_path?.institution_name)
              : "Across all children"
            }
            color={isMilitaryPath(selectedChild) ? "emerald" : "blue"}
            size="large"
          />
          <KPICard
            icon={isMilitaryPath(selectedChild) ? Award : PiggyBank}
            label={selectedChild 
              ? (isMilitaryPath(selectedChild) ? "Est. Benefits Value" : "Amount Funded")
              : "Current Savings"
            }
            value={isMilitaryPath(selectedChild) 
              ? formatCurrency(displaySummary?.military_benefits)
              : formatCurrency(displaySummary?.total_current_savings)
            }
            subLabel={selectedChild 
              ? (isMilitaryPath(selectedChild) ? "GI Bill + Bonuses + Housing" : "Currently allocated")
              : `${formatCurrency(portfolio?.monthly_savings_rate)}/month`
            }
            color="emerald"
            size="large"
          />
          <KPICard
            icon={isMilitaryPath(selectedChild) ? CheckCircle : AlertTriangle}
            label={isMilitaryPath(selectedChild) ? "Funding Status" : "Funding Shortfall"}
            value={isMilitaryPath(selectedChild) ? "Fully Covered" : formatCurrency(displaySummary?.total_funding_shortfall)}
            subLabel={selectedChild 
              ? (isMilitaryPath(selectedChild) 
                ? "No out-of-pocket costs"
                : `${formatPercent(selectedChild.funding_gap_analysis?.gap_percentage || 0)} remaining`)
              : `Need ${formatCurrency(family_summary?.monthly_contribution_required)}/mo`
            }
            color={isMilitaryPath(selectedChild) ? "emerald" : "amber"}
            size="large"
          />
          <KPICard
            icon={Shield}
            label="Family Stability Score"
            value={displaySummary?.family_stability_score || 0}
            subLabel="Out of 100"
            color="purple"
            size="large"
          />
        </div>
        
        {/* Selected Child Career Details Panel - AFTER KPIs */}
        {selectedChild && <ChildCareerPanel child={selectedChild} />}
        
        {/* Tabs - Compact pink style */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="mb-2">
            <p className="text-slate-300 text-xs flex items-center gap-1">
              <Info className="w-3 h-3" />
              Click tabs to explore different views
            </p>
          </div>
          <TabsList className="bg-slate-800/60 p-1 mb-6 inline-flex gap-1 rounded-lg border border-pink-500/20">
            <TabsTrigger 
              value="overview" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-rose-500 data-[state=active]:text-white data-[state=inactive]:text-slate-300 data-[state=inactive]:hover:text-pink-300 transition-all px-3 py-1.5 rounded-md text-sm font-medium"
            >
              <Home className="w-3.5 h-3.5 mr-1.5" /> Overview
            </TabsTrigger>
            <TabsTrigger 
              value="portfolio" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-rose-500 data-[state=active]:text-white data-[state=inactive]:text-slate-300 data-[state=inactive]:hover:text-pink-300 transition-all px-3 py-1.5 rounded-md text-sm font-medium"
            >
              <Wallet className="w-3.5 h-3.5 mr-1.5" /> Portfolio
            </TabsTrigger>
            <TabsTrigger 
              value="credit" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-rose-500 data-[state=active]:text-white data-[state=inactive]:text-slate-300 data-[state=inactive]:hover:text-pink-300 transition-all px-3 py-1.5 rounded-md text-sm font-medium"
            >
              <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Credit & Loans
            </TabsTrigger>
            <TabsTrigger 
              value="scenarios" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-rose-500 data-[state=active]:text-white data-[state=inactive]:text-slate-300 data-[state=inactive]:hover:text-pink-300 transition-all px-3 py-1.5 rounded-md text-sm font-medium"
            >
              <Calculator className="w-3.5 h-3.5 mr-1.5" /> Scenarios
            </TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Children Exposure Chart */}
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                  Education Cost by Child
                </h3>
                {exposureChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={exposureChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="name" stroke="#C6D4E8" />
                      <YAxis stroke="#C6D4E8" tickFormatter={(v) => `$${v/1000}k`} />
                      <Tooltip 
                        contentStyle={{ background: '#1e293b', border: 'none', borderRadius: '8px' }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Bar dataKey="cost" name="Total Cost" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="funded" name="Funded" fill="#10B981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center">
                    <p className="text-slate-300">No path cost data available</p>
                  </div>
                )}
              </GlassCard>
              
              {/* Savings Allocation */}
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-emerald-400" />
                  Savings Allocation
                </h3>
                {accountsChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPieChart>
                      <Pie
                        data={accountsChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {accountsChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ background: '#1e293b', border: 'none', borderRadius: '8px' }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center">
                    <p className="text-slate-300">No linked accounts</p>
                  </div>
                )}
              </GlassCard>
              
              {/* Quick Actions */}
              <GlassCard className="p-6 lg:col-span-2">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  Recommendations
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {(displaySummary?.total_funding_shortfall || 0) > 0 && (
                    <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                      <AlertTriangle className="w-8 h-8 text-amber-400 mb-3" />
                      <h4 className="text-white font-medium mb-1">Close Funding Gap</h4>
                      <p className="text-slate-300 text-sm">
                        {selectedChild 
                          ? `Increase savings by ${formatCurrency((selectedChild.funding_gap_analysis?.funding_gap || 0) / 48)}/mo to close ${selectedChild.child_name}'s gap in 4 years.`
                          : `Increase monthly savings by ${formatCurrency((family_summary?.monthly_contribution_required || 0) - (portfolio?.monthly_savings_rate || 0))} to close the gap in 4 years.`
                        }
                      </p>
                    </div>
                  )}
                  <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <TrendingUp className="w-8 h-8 text-blue-400 mb-3" />
                    <h4 className="text-white font-medium mb-1">Scholarship Strategy</h4>
                    <p className="text-slate-300 text-sm">
                      {selectedChild 
                        ? `Based on ${selectedChild.child_name}'s profile, they may qualify for merit-based aid.`
                        : 'Based on your children\'s profiles, they may qualify for merit-based aid. Review scholarship opportunities.'
                      }
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <Target className="w-8 h-8 text-emerald-400 mb-3" />
                    <h4 className="text-white font-medium mb-1">Path Comparison</h4>
                    <p className="text-slate-300 text-sm">
                      Compare different education paths to find the best ROI for your family's situation.
                    </p>
                  </div>
                </div>
              </GlassCard>
            </div>
          </TabsContent>
          
          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Linked Accounts */}
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Landmark className="w-5 h-5 text-blue-400" />
                    Linked Accounts
                  </h3>
                  <Button size="sm" variant="outline" className="border-slate-600" onClick={handleRefreshAccounts}>
                    <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {portfolio?.linked_accounts?.map((account) => (
                    <AccountCard key={account.id} account={account} />
                  ))}
                </div>
                
                <div className="mt-4 p-4 rounded-xl bg-slate-900/50 border border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Total Available</span>
                    <span className="text-xl font-bold text-white">{formatCurrency(portfolio?.total_available_funds)}</span>
                  </div>
                </div>
              </GlassCard>
              
              {/* Portfolio Summary */}
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-400" />
                  Portfolio Summary
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-slate-300">Education Savings (529)</span>
                      <span className="text-emerald-400 font-medium">{formatCurrency(portfolio?.total_education_savings)}</span>
                    </div>
                    <Progress value={(portfolio?.total_education_savings / (family_summary?.total_education_exposure || 1)) * 100} className="h-2" />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-slate-800/50">
                      <p className="text-slate-300 text-xs mb-1">Monthly Savings Rate</p>
                      <p className="text-xl font-bold text-white">{formatCurrency(portfolio?.monthly_savings_rate)}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-slate-800/50">
                      <p className="text-slate-300 text-xs mb-1">Projected Growth</p>
                      <p className="text-xl font-bold text-emerald-400">{((portfolio?.projected_growth_rate || 0) * 100).toFixed(0)}%</p>
                    </div>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                    <div className="flex items-center gap-2">
                      <Landmark className="w-4 h-4 text-slate-300" />
                      <span className="text-slate-300 text-sm">Automatic bank syncing via Plaid, coming soon</span>
                    </div>
                  </div>
                </div>
              </GlassCard>
            </div>
          </TabsContent>
          
          {/* Credit & Loans Tab */}
          <TabsContent value="credit">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Credit Score */}
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-purple-400" />
                  Credit Score
                </h3>
                
                {creditProfile ? (
                  <>
                    <CreditScoreGauge score={creditProfile.credit_score} rating={creditProfile.credit_rating} />
                    
                    <div className="mt-4 space-y-2">
                      <p className="text-slate-300 text-sm">Score Factors:</p>
                      {creditProfile.score_factors?.map((factor, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                          <span className="text-slate-300">{factor}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg bg-slate-800/50">
                        <p className="text-slate-300 text-xs">Available Credit</p>
                        <p className="text-white font-medium">{formatCurrency(creditProfile.available_credit)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-800/50">
                        <p className="text-slate-300 text-xs">Utilization</p>
                        <p className="text-white font-medium">{creditProfile.credit_utilization?.toFixed(0)}%</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-300">Credit data not available</p>
                  </div>
                )}
              </GlassCard>
              
              {/* Loan Eligibility */}
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                  Loan Eligibility
                </h3>
                
                {creditProfile?.loan_eligibility ? (
                  <div className="space-y-3">
                    {Object.entries(creditProfile.loan_eligibility).map(([loan, details]) => (
                      <LoanEligibilityCard key={loan} loan={loan} details={details} />
                    ))}
                    
                    <div className="mt-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-amber-400" />
                        <span className="text-amber-400 font-medium text-sm">Important Note</span>
                      </div>
                      <p className="text-slate-300 text-sm">
                        Loan eligibility is based on your credit profile. Actual terms may vary based on lender requirements. Consider federal loans first for better rates and protections.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-300">Loan eligibility data not available</p>
                  </div>
                )}
              </GlassCard>
            </div>
          </TabsContent>
          
          {/* Scenarios Tab */}
          <TabsContent value="scenarios">
            <ScenarioComparisonTool />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Manual Financial Entry Modal */}
      <ManualFinancialEntryModal
        open={showManualEntry}
        onClose={() => setShowManualEntry(false)}
        onSubmit={handleManualUpdate}
        existingData={portfolio}
      />
    </AppLayout>
  );
}
